// Centralized API-error → human message → toast helper.
//
// Every network call in the app can fail in a handful of distinct ways
// (HTTP 4xx/5xx with a body, HTTP error with only a status, a timeout, or a
// "can't reach the server" network error that carries NO response at all).
// The old `handleErrors` only spoke the first dialect and silently ignored the
// rest, so offline/timeout failures showed the user nothing. This module turns
// any of those shapes into one friendly sentence and (optionally) toasts it via
// the framework-agnostic toast bus so React and non-React modules behave alike.

import toast from "../lib/toast";

const DEFAULT_FALLBACK = "Something went wrong. Please try again.";

/**
 * Extract a human-readable message from any error thrown by axios / fetch / JS.
 * @param {*} error    The caught error (axios error, Error, string, anything).
 * @param {string} [fallback] Message to use when nothing better can be derived.
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = DEFAULT_FALLBACK) {
  if (!error) return fallback;
  if (typeof error === "string") return error.trim() || fallback;

  // 1) axios/HTTP error that carries a server response.
  const response = error.response;
  if (response) {
    const data = response.data;
    if (data) {
      if (typeof data === "string" && data.trim()) return data.trim();
      if (data.error) return String(data.error);
      if (data.message) return String(data.message);
      if (data.errors && typeof data.errors === "object") {
        const first = Object.values(data.errors)
          .flat()
          .find(Boolean);
        if (first) return String(first);
      }
    }

    const status = response.status;
    if (status === 401 || status === 403) {
      return "Your session has expired or you're not authorized. Please refresh and sign in again.";
    }
    if (status === 404) return "The requested data could not be found.";
    if (status === 429) return "Too many requests. Please wait a moment and try again.";
    if (status >= 500) {
      return "The server ran into a problem. Please try again in a moment.";
    }
    // Any other status with no usable body: keep it human. The raw status /
    // statusText is already logged to the console by notifyApiError for devs —
    // the user just needs a plain "try again" message, not HTTP jargon.
    return fallback;
  }

  // 2) axios/fetch error with NO response — timeout or network unreachable.
  const code = error.code;
  const rawMessage = String(error.message || "");
  if (code === "ECONNABORTED" || /timeout/i.test(rawMessage)) {
    return "The request timed out. Please check your connection and try again.";
  }
  if (
    code === "ERR_NETWORK" ||
    rawMessage === "Network Error" ||
    /failed to fetch/i.test(rawMessage)
  ) {
    return "Can't reach the server. Please check your internet connection and try again.";
  }

  // 3) plain JS Error or anything else.
  if (rawMessage) return rawMessage;
  return fallback;
}

/**
 * Convert an error into a friendly message and show it as an error toast.
 * @param {*} error
 * @param {object} [options]
 * @param {string} [options.fallback]    Message when nothing better is derivable.
 * @param {string} [options.description] Optional second line on the toast.
 * @param {string} [options.key]         Stable toast key — reuse to dedupe
 *                                       repeated failures of the same feature.
 * @param {number} [options.duration=10000] Auto-dismiss in ms.
 * @param {{label: string, onClick: function}} [options.action] Optional action
 *                                       button (e.g. Retry).
 * @param {boolean} [options.log=true]   Also console.error the raw error.
 * @returns {string} The message that was shown.
 */
export function notifyApiError(error, options = {}) {
  const {
    fallback = DEFAULT_FALLBACK,
    description,
    key,
    duration = 10000,
    action,
    log = true,
  } = options;

  const messageText = getApiErrorMessage(error, fallback);
  if (log) console.error(messageText, error);
  toast.error(messageText, { key, description, duration, action });
  return messageText;
}

export default notifyApiError;
